import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../store'
import {
  CrossClientQueryExecution,
  CrossClientQueryExecutionProgress,
  CrossClientQueryInitializedExecution,
  CrossClientQueryPhases,
} from '../../models/CrossClient'

interface CrossClientLandingState {
  executions: EntityState<CrossClientQueryExecution, string>
  recentExecutionsLoading: boolean
  initLoading: boolean
  evaluateLoading: boolean
  currentExecution: CrossClientQueryInitializedExecution | null
  runningExecutionProgress: EntityState<CrossClientQueryExecutionProgress, string>
  pollingEnabled: boolean
}

const crossClientExecutionsAdapter = createEntityAdapter<CrossClientQueryExecution>()
const crossClientRunningExecutionProgressAdapter = createEntityAdapter<CrossClientQueryExecutionProgress, string>({ selectId: (e) => e.executionId })

const initialState: CrossClientLandingState = {
  executions: crossClientExecutionsAdapter.getInitialState(),
  recentExecutionsLoading: true,
  initLoading: false,
  evaluateLoading: false,
  currentExecution: null,
  runningExecutionProgress: crossClientRunningExecutionProgressAdapter.getInitialState(),
  pollingEnabled: false,
}

export const crossClientLandingSlice = createSlice({
  name: 'crossClientLanding',
  initialState,
  reducers: {
    setCrossClientRecentExecutionsLoading: (state, action: PayloadAction<boolean>) => {
      state.recentExecutionsLoading = action.payload
    },
    setCrossClientInitLoading: (state, action: PayloadAction<boolean>) => {
      state.initLoading = action.payload
    },
    setCrossClientEvaluateLoading: (state, action: PayloadAction<boolean>) => {
      state.evaluateLoading = action.payload
    },
    setCrossClientExecutions: (state, action: PayloadAction<CrossClientQueryExecution[]>) => {
      crossClientExecutionsAdapter.setMany(state.executions, action.payload)
    },
    setCrossClientLandingExecution: (state, action: PayloadAction<CrossClientQueryInitializedExecution | null>) => {
      state.currentExecution = action.payload
    },
    setCrossClientRunningExecutionProgress: (state, action: PayloadAction<CrossClientQueryExecutionProgress[]>) => {
      crossClientRunningExecutionProgressAdapter.setMany(state.runningExecutionProgress, action.payload)
      const finishedProgresses = action.payload.filter((e) => e.phase !== CrossClientQueryPhases.PROCESSING)
      crossClientExecutionsAdapter.updateMany(
        state.executions,
        finishedProgresses.map((progress) => {
          return { id: progress.executionId, changes: { phase: progress.phase } }
        }),
      )
    },
    startCrossClientLandingPolling: (state) => {
      state.pollingEnabled = true
    },
    stopCrossClientLandingPolling: (state) => {
      state.pollingEnabled = false
    },
    resetCrossClientLanding: () => initialState,
  },
})

export const {
  setCrossClientRecentExecutionsLoading,
  setCrossClientExecutions,
  setCrossClientInitLoading,
  setCrossClientEvaluateLoading,
  setCrossClientLandingExecution,
  startCrossClientLandingPolling,
  stopCrossClientLandingPolling,
  setCrossClientRunningExecutionProgress,
  resetCrossClientLanding,
} = crossClientLandingSlice.actions

const crossClientExecutionSelectors = crossClientExecutionsAdapter.getSelectors((state: RootState) => state.crossClientLanding.executions)

export const selectCrossClientExecutions = (state: RootState) => crossClientExecutionSelectors.selectAll(state)

export const selectCrossClientRecentExecutions = createSelector([selectCrossClientExecutions], (crossClientExecutions) => {
  return crossClientExecutions.slice(0, 5)
})

export const selectCrossClientRunningExecutions = createSelector([selectCrossClientExecutions], (crossClientExecutions) => {
  return crossClientExecutions.filter((e) => e.phase === CrossClientQueryPhases.PROCESSING)
})

export const selectCrossClientRecentExecutionsLoading = (state: RootState) => state.crossClientLanding.recentExecutionsLoading

export const selectCrossClientInitLoading = (state: RootState) => state.crossClientLanding.initLoading

export const selectCrossClientEvaluateLoading = (state: RootState) => state.crossClientLanding.evaluateLoading

export const selectCrossClientLandingCurrentExecution = (state: RootState) => state.crossClientLanding.currentExecution

export const selectCrossClientLandingPollingEnabled = (state: RootState) => state.crossClientLanding.pollingEnabled

export default crossClientLandingSlice.reducer
